import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ServiceCard from './ServiceCard';
import { API } from '../config/api';
import { Plus, Loader2, X, Wrench } from 'lucide-react';

const CATEGORIES = [
  { value: 'recording', label: 'Recording Session' },
  { value: 'mixing', label: 'Mixing' },
  { value: 'mastering', label: 'Mastering' },
  { value: 'studio_time', label: 'Studio Time' },
  { value: 'production', label: 'Production' },
  { value: 'other', label: 'Other' },
];

const emptyForm = { title: '', description: '', category: '', price: '', price_type: 'fixed', duration: '', includes: '' };

export default function ServiceManager() {
  const { getAuthHeaders } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API}/services/me`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setServices(d.services || []); }
    } catch (e) { console.error('Fetch services error:', e); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSubmitting(true);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        price: form.price ? parseFloat(form.price) : null,
        price_type: form.price_type,
        duration: form.duration.trim() || null,
        includes: form.includes ? form.includes.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      const url = editingId ? `${API}/services/${editingId}` : `${API}/services`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Failed to save service');
      toast.success(editingId ? 'Service updated!' : 'Service created!');
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchServices();
    } catch (err) {
      toast.error(err.message);
    }
    setSubmitting(false);
  };

  const handleEdit = (svc) => {
    setForm({
      title: svc.title || '',
      description: svc.description || '',
      category: svc.category || '',
      price: svc.price != null ? String(svc.price) : '',
      price_type: svc.price_type || 'fixed',
      duration: svc.duration || '',
      includes: (svc.includes || []).join(', '),
    });
    setEditingId(svc.service_id);
    setShowForm(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      const res = await fetch(`${API}/services/${serviceId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Service deleted');
      fetchServices();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div data-testid="service-manager">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Wrench className="w-5 h-5" />Your Services</h3>
        <Button size="sm" onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }} data-testid="add-service-btn">
          <Plus className="w-4 h-4 mr-1" />Add Service
        </Button>
      </div>

      {/* Service Form */}
      {showForm && (
        <Card className="glass-card border-border p-5 mb-5" data-testid="service-form">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium">{editingId ? 'Edit Service' : 'New Service'}</h4>
            <Button variant="ghost" size="icon" onClick={() => { setShowForm(false); setEditingId(null); }}><X className="w-4 h-4" /></Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input placeholder="Service title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="service-title-input" />
            <Textarea placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} data-testid="service-desc-input" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="service-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" placeholder="Price ($)" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} data-testid="service-price-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Duration (e.g., 2 hours)" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} data-testid="service-duration-input" />
              <Select value={form.price_type} onValueChange={v => setForm(f => ({ ...f, price_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed Price</SelectItem>
                  <SelectItem value="hourly">Per Hour</SelectItem>
                  <SelectItem value="negotiable">Negotiable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input placeholder="What's included (comma separated)" value={form.includes} onChange={e => setForm(f => ({ ...f, includes: e.target.value }))} data-testid="service-includes-input" />
            <Button type="submit" disabled={submitting} className="w-full" data-testid="save-service-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingId ? 'Update Service' : 'Create Service'}
            </Button>
          </form>
        </Card>
      )}

      {/* Service List */}
      {services.length === 0 ? (
        <Card className="glass-card border-border p-8 text-center">
          <Wrench className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No services yet. Add your first service offering!</p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {services.map(svc => (
            <ServiceCard key={svc.service_id} service={svc} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
}
